/**
 * Channels Category Mapping Handlers
 *
 * Pure handlers for the test-channel category mapper. All handlers operate on
 * the `channelDetail.categories` / `channelDetail.categorySearch` slices and
 * have NO side effects — the policy layer persists mappings to the backend
 * aggregate and the subscription snapshot reconciles the authoritative state.
 *
 * `map` / `unmap` are optimistic: they flip `isMapped` / `mappingSource` and set
 * (or clear) the teselly category fields locally, but always spread `...category`
 * first so the row's vector `suggestions` are preserved across map/unmap. They
 * ALSO enqueue a `CategoryWriteIntent` in `pendingCategoryWrites` — the ONLY
 * channel through which the policy pushes a write to the backend. The `set`
 * (api) reconciliation never enqueues, so an `api` snapshot of already-mapped
 * rows can never be mistaken for a user edit (see `CategoryWriteIntent`).
 */

import {
  ChannelsState,
  CategoryWriteIntent,
  categoryIntentKey,
  clampCategoriesListPage,
  filterChannelCategories
} from './state';
import { ChannelsCommand } from './commands';

type ExtractCommand<T extends string> = Extract<ChannelsCommand, { type: T }>;

/**
 * Append a pending write intent, replacing any prior intent for the same
 * `externalId` (last write wins — one pending intent per channel category).
 */
const enqueueIntent = (
  pending: CategoryWriteIntent[],
  intent: CategoryWriteIntent
): CategoryWriteIntent[] => [
  ...pending.filter(( p ) => p.externalId !== intent.externalId ),
  intent
];

/**
 * Replace the full category list (subscription snapshot / initial load).
 */
const listFilteredCount = ( state: ChannelsState, categories = state.channelDetail.categories ) =>
  filterChannelCategories( categories, state.channelDetail.categoriesListView ).length;

const toggleArrayValue = <T extends string>( values: T[], value: T ): T[] =>
  values.includes( value ) ? values.filter(( v ) => v !== value ) : [ ...values, value ];

const handleCategoriesSet = (
  state: ChannelsState,
  cmd: ExtractCommand<'integrations/channels/categories/set'>
): ChannelsState => ({
  ...state,
  channelDetail: {
    ...state.channelDetail,
    categories: cmd.payload.categories,
    categoriesListView: clampCategoriesListPage(
      state.channelDetail.categoriesListView,
      filterChannelCategories( cmd.payload.categories, state.channelDetail.categoriesListView ).length
    )
  }
});

/**
 * Optimistic local mapping; the policy persists via the backend aggregate and
 * the subscription snapshot reconciles the authoritative state. Spreads
 * `...category` so the row's `suggestions` are NOT dropped.
 */
const handleCategoryMap = (
  state: ChannelsState,
  cmd: ExtractCommand<'integrations/channels/categories/map'>
): ChannelsState => ({
  ...state,
  channelDetail: {
    ...state.channelDetail,
    categories: state.channelDetail.categories.map(( category ) =>
      category.externalId === cmd.payload.externalId
        ? {
          ...category,
          tesellyCategoryId: cmd.payload.tesellyCategoryId,
          tesellyCategoryName: cmd.payload.tesellyCategoryName,
          isMapped: true,
          mappingSource: 'manual'
        }
        : category ),
    pendingCategoryWrites: enqueueIntent( state.channelDetail.pendingCategoryWrites, {
      kind: 'map',
      externalId: cmd.payload.externalId,
      tesellyCategoryId: cmd.payload.tesellyCategoryId,
      tesellyCategoryName: cmd.payload.tesellyCategoryName
    })
  }
});

/**
 * Optimistic local unmapping; clears the teselly category fields and resets
 * `mappingSource` to `null`. Spreads `...category` so the row's `suggestions`
 * are NOT dropped.
 */
const handleCategoryUnmap = (
  state: ChannelsState,
  cmd: ExtractCommand<'integrations/channels/categories/unmap'>
): ChannelsState => ({
  ...state,
  channelDetail: {
    ...state.channelDetail,
    categories: state.channelDetail.categories.map(( category ) =>
      category.externalId === cmd.payload.externalId
        ? {
          ...category,
          tesellyCategoryId: null,
          tesellyCategoryName: null,
          isMapped: false,
          mappingSource: null
        }
        : category ),
    pendingCategoryWrites: enqueueIntent( state.channelDetail.pendingCategoryWrites, {
      kind: 'unmap',
      externalId: cmd.payload.externalId
    })
  }
});

/**
 * Set the ML-category search query for a specific channel-category row (the
 * policy reacts and fetches results). `externalId` scopes the search to the
 * active row's picker. When the active row CHANGES (the user moved to a
 * different picker), the prior row's results/pagination are discarded so stale
 * results never flash in the new row's dropdown.
 */
const handleCategorySearchSet = (
  state: ChannelsState,
  cmd: ExtractCommand<'integrations/channels/categories/search/set'>
): ChannelsState => {
  const { query, externalId } = cmd.payload;
  const rowChanged = state.channelDetail.categorySearch.activeExternalId !== externalId;
  return {
    ...state,
    channelDetail: {
      ...state.channelDetail,
      categorySearch: {
        ...state.channelDetail.categorySearch,
        activeExternalId: externalId,
        query,
        ...( rowChanged
          ? { results: [], offset: 0, hasMore: false, pendingLoadMore: false }
          : {})
      }
    }
  };
};

/**
 * Replace search results (api-sourced, from the initial/debounced search).
 * Resets pagination so the next scroll starts from the right offset.
 */
const handleCategorySearchResultsSet = (
  state: ChannelsState,
  cmd: ExtractCommand<'integrations/channels/categories/search/results/set'>
): ChannelsState => ({
  ...state,
  channelDetail: {
    ...state.channelDetail,
    categorySearch: {
      ...state.channelDetail.categorySearch,
      results: cmd.payload.results,
      offset: cmd.payload.results.length,
      hasMore: cmd.payload.hasMore,
      pendingLoadMore: false
    }
  }
});

/**
 * Append the next page of search results (infinite scroll). Deduplicates by
 * `id` so the two-pass initial fetch and subsequent offset pages never repeat.
 * Clears `pendingLoadMore` once the policy has fulfilled the request.
 */
const handleCategorySearchResultsAppend = (
  state: ChannelsState,
  cmd: ExtractCommand<'integrations/channels/categories/search/results/append'>
): ChannelsState => {
  const existingIds = new Set(
    state.channelDetail.categorySearch.results.map(( r ) => r.id )
  );
  const fresh = cmd.payload.results.filter(( r ) => !existingIds.has( r.id ));
  const merged = [ ...state.channelDetail.categorySearch.results, ...fresh ];
  return {
    ...state,
    channelDetail: {
      ...state.channelDetail,
      categorySearch: {
        ...state.channelDetail.categorySearch,
        results: merged,
        offset: merged.length,
        hasMore: cmd.payload.hasMore,
        pendingLoadMore: false
      }
    }
  };
};

/**
 * User-sourced: request the next page of results (infinite scroll). Sets the
 * `pendingLoadMore` flag the policy drains. Guards against double-set so the
 * state reference doesn't change while a load is already in flight.
 */
const handleCategorySearchLoadMore = (
  state: ChannelsState,
  _cmd: ExtractCommand<'integrations/channels/categories/search/loadMore'>
): ChannelsState => {
  if ( state.channelDetail.categorySearch.pendingLoadMore ) return state;
  return {
    ...state,
    channelDetail: {
      ...state.channelDetail,
      categorySearch: {
        ...state.channelDetail.categorySearch,
        pendingLoadMore: true
      }
    }
  };
};

/**
 * Drop the pending intents the policy has dispatched to the backend. Removes
 * only intents whose `categoryIntentKey` is in `keys`, so a newer intent for the
 * same `externalId` enqueued mid-flight (different key) survives and is drained
 * on the next tick.
 */
const handlePendingClear = (
  state: ChannelsState,
  cmd: ExtractCommand<'integrations/channels/categories/pending/clear'>
): ChannelsState => {
  const cleared = new Set( cmd.payload.keys );
  return {
    ...state,
    channelDetail: {
      ...state.channelDetail,
      pendingCategoryWrites: state.channelDetail.pendingCategoryWrites.filter(
        ( intent ) => !cleared.has( categoryIntentKey( intent ))
      )
    }
  };
};

/**
 * Loading setter for the categories list.
 */
const handleSetCategoriesLoading = (
  state: ChannelsState,
  cmd: ExtractCommand<'integrations/channels/categories/loading/setCategories'>
): ChannelsState => ({
  ...state,
  channelDetail: {
    ...state.channelDetail,
    loading: {
      ...state.channelDetail.loading,
      categoriesLoading: cmd.payload.isLoading
    }
  }
});

/**
 * Loading setter for the "load more" spinner (infinite scroll).
 */
const handleSetCategorySearchLoadingMore = (
  state: ChannelsState,
  cmd: ExtractCommand<'integrations/channels/categories/loading/setSearchLoadingMore'>
): ChannelsState => ({
  ...state,
  channelDetail: {
    ...state.channelDetail,
    loading: {
      ...state.channelDetail.loading,
      categorySearchLoadingMore: cmd.payload.isLoading
    }
  }
});

/**
 * Loading setter for the ML-category search.
 */
const handleSetCategorySearchLoading = (
  state: ChannelsState,
  cmd: ExtractCommand<'integrations/channels/categories/loading/setSearch'>
): ChannelsState => ({
  ...state,
  channelDetail: {
    ...state.channelDetail,
    loading: {
      ...state.channelDetail.loading,
      categorySearchLoading: cmd.payload.isLoading
    }
  }
});

/**
 * User-sourced: request a re-pull of the channel catalog. Sets the pending-resync
 * flag the policy drains. Like map/unmap, this is the ONLY channel through which
 * the policy triggers a backend resync — `api` reconciliation never sets it.
 */
const handleCategoryResync = (
  state: ChannelsState,
  _cmd: ExtractCommand<'integrations/channels/categories/resync'>
): ChannelsState => ({
  ...state,
  channelDetail: {
    ...state.channelDetail,
    pendingCategoryResync: true
  }
});

/**
 * Policy-only bookkeeping: clear the drained resync intent.
 */
const handleCategoryResyncClear = (
  state: ChannelsState,
  _cmd: ExtractCommand<'integrations/channels/categories/resync/clear'>
): ChannelsState => ({
  ...state,
  channelDetail: {
    ...state.channelDetail,
    pendingCategoryResync: false
  }
});

/**
 * Record (or clear) the `map-channel-categories` run awaiting review for the
 * current integration. api-sourced — the categories policy owns the query.
 */
const handleAiRunSet = (
  state: ChannelsState,
  cmd: ExtractCommand<'integrations/channels/categories/aiRun/set'>
): ChannelsState => ({
  ...state,
  channelDetail: {
    ...state.channelDetail,
    aiMappingReadyRunId: cmd.payload.runId
  }
});

/**
 * Loading setter for the resync action.
 */
const handleSetCategoriesResyncing = (
  state: ChannelsState,
  cmd: ExtractCommand<'integrations/channels/categories/loading/setResyncing'>
): ChannelsState => ({
  ...state,
  channelDetail: {
    ...state.channelDetail,
    loading: {
      ...state.channelDetail.loading,
      categoriesResyncing: cmd.payload.isLoading
    }
  }
});

const handleCategoriesListSetPage = (
  state: ChannelsState,
  cmd: ExtractCommand<'integrations/channels/categories/list/setPage'>
): ChannelsState => ({
  ...state,
  channelDetail: {
    ...state.channelDetail,
    categoriesListView: clampCategoriesListPage(
      { ...state.channelDetail.categoriesListView, page: cmd.payload.page },
      listFilteredCount( state )
    )
  }
});

const handleCategoriesListSetSort = (
  state: ChannelsState,
  cmd: ExtractCommand<'integrations/channels/categories/list/setSort'>
): ChannelsState => ({
  ...state,
  channelDetail: {
    ...state.channelDetail,
    categoriesListView: clampCategoriesListPage(
      {
        ...state.channelDetail.categoriesListView,
        page: 1,
        sort: cmd.payload.sort
      },
      listFilteredCount( state )
    )
  }
});

const handleCategoriesListSetSearchQuery = (
  state: ChannelsState,
  cmd: ExtractCommand<'integrations/channels/categories/list/setSearchQuery'>
): ChannelsState => {
  const categoriesListView = {
    ...state.channelDetail.categoriesListView,
    page: 1,
    searchQuery: cmd.payload.query
  };
  return {
    ...state,
    channelDetail: {
      ...state.channelDetail,
      categoriesListView: clampCategoriesListPage(
        categoriesListView,
        filterChannelCategories( state.channelDetail.categories, categoriesListView ).length
      )
    }
  };
};

const handleCategoriesListToggleMappingStatus = (
  state: ChannelsState,
  cmd: ExtractCommand<'integrations/channels/categories/list/toggleMappingStatus'>
): ChannelsState => {
  const categoriesListView = {
    ...state.channelDetail.categoriesListView,
    page: 1,
    selectedMappingStatuses: toggleArrayValue(
      state.channelDetail.categoriesListView.selectedMappingStatuses,
      cmd.payload.status
    )
  };
  return {
    ...state,
    channelDetail: {
      ...state.channelDetail,
      categoriesListView: clampCategoriesListPage(
        categoriesListView,
        filterChannelCategories( state.channelDetail.categories, categoriesListView ).length
      )
    }
  };
};

const handleCategoriesListClearFilters = (
  state: ChannelsState,
  _cmd: ExtractCommand<'integrations/channels/categories/list/clearFilters'>
): ChannelsState => ({
  ...state,
  channelDetail: {
    ...state.channelDetail,
    categoriesListView: clampCategoriesListPage(
      {
        ...state.channelDetail.categoriesListView,
        page: 1,
        searchQuery: '',
        selectedMappingStatuses: []
      },
      state.channelDetail.categories.length
    )
  }
});

/**
 * Category-mapping handler map fragment, spread into the domain reducer in
 * `handlers.ts`.
 */
export const categoriesHandlers = {
  'integrations/channels/categories/set': handleCategoriesSet,
  'integrations/channels/categories/map': handleCategoryMap,
  'integrations/channels/categories/unmap': handleCategoryUnmap,
  'integrations/channels/categories/search/set': handleCategorySearchSet,
  'integrations/channels/categories/search/results/set': handleCategorySearchResultsSet,
  'integrations/channels/categories/search/results/append': handleCategorySearchResultsAppend,
  'integrations/channels/categories/search/loadMore': handleCategorySearchLoadMore,
  'integrations/channels/categories/pending/clear': handlePendingClear,
  'integrations/channels/categories/resync': handleCategoryResync,
  'integrations/channels/categories/resync/clear': handleCategoryResyncClear,
  'integrations/channels/categories/aiRun/set': handleAiRunSet,
  'integrations/channels/categories/list/setPage': handleCategoriesListSetPage,
  'integrations/channels/categories/list/setSort': handleCategoriesListSetSort,
  'integrations/channels/categories/list/setSearchQuery': handleCategoriesListSetSearchQuery,
  'integrations/channels/categories/list/toggleMappingStatus': handleCategoriesListToggleMappingStatus,
  'integrations/channels/categories/list/clearFilters': handleCategoriesListClearFilters,
  'integrations/channels/categories/loading/setCategories': handleSetCategoriesLoading,
  'integrations/channels/categories/loading/setSearch': handleSetCategorySearchLoading,
  'integrations/channels/categories/loading/setSearchLoadingMore': handleSetCategorySearchLoadingMore,
  'integrations/channels/categories/loading/setResyncing': handleSetCategoriesResyncing
} as const;
